import * as THREE from 'three';
import { BUS } from '../constants/gameSettings';

class AudioManager {
    constructor(camera, scene) {
        this.camera = camera;
        this.scene = scene;
        this.audioLoader = new THREE.AudioLoader();
        this.listener = new THREE.AudioListener();
        this.sounds = {};
        this.camera.add(this.listener);
        
        // Settings
        this.masterVolume = 1.0;
        this.isMuted = false;
        this.initialized = false;
        this.isIdleEnabled = true;
        
        // Track missing sound files to avoid 404 errors
        this.missingAudioFiles = {
            'collision_hard': true
        };
        
        // Disable collision sounds but keep coins and laser
        this.collisionSoundsEnabled = false;
    }
    
    async init() {
        try {
            // Configure audio settings
            this.listener.setMasterVolume(this.masterVolume);
            
            // Load all required sounds
            await Promise.all([
                this.loadSound('engine_idle', '/audio/engine_idle.mp3', true, 0.0),
                this.loadSound('engine_accelerate', '/audio/engine_accelerate.mp3', false, 0.4),
                this.loadSound('engine_steady', '/audio/engine_steady.mp3', true, 0.5),
                this.loadSound('braking', '/audio/braking.mp3', false, 0.6),
                this.loadSound('horn', '/audio/horn.mp3', false, 0.8),
                this.loadSound('engine_idle_off', '/audio/engine_idle_to_off.mp3', true, 0.4),
                
                // Still load collision sounds but don't play them
                // This prevents errors if other parts of the code reference them
                this.loadSound('collision_soft', '/audio/collision_soft.mp3', false, 0.7),
                this.loadSound('collision_medium', '/audio/collision_medium.mp3', false, 0.8),
                this.loadFallbackSound('collision_hard', false, 1.0),
                
                // Important sounds that should still work
                this.loadSound('coin_collected', '/audio/coin_collected.mp3', false, 0.5),
                this.loadSound('laser', '/audio/laser.mp3', false, 0.8),
                this.loadSound('cube_explode', '/audio/cube_explode.mp3', false, 0.6),
                
                // Meteor sound (using fallback in case file doesn't exist)
                this.loadFallbackSound('meteor', false, 0.8),
            ]);
            
            this.initialized = true;
            return true;
        } catch (error) {
            // Create fallback sounds if loading fails
            this.createFallbackSounds();
            return false;
        }
    }
    
    // New method to create a fallback for specific missing sounds
    loadFallbackSound(id, loop = false, volume = 0.5) {
        if (this.missingAudioFiles[id]) {
            // Create a fallback sound
            const sound = new THREE.PositionalAudio(this.listener);
            
            // Create a fallback buffer (use collision_medium as fallback or create one)
            if (this.sounds['collision_medium'] && this.sounds['collision_medium'].sound.buffer) {
                // If medium collision sound is loaded, use that
                sound.setBuffer(this.sounds['collision_medium'].sound.buffer);
            } else {
                // Otherwise create a synthetic sound
                const ctx = THREE.AudioContext.getContext();
                const sampleRate = ctx.sampleRate;
                const buffer = ctx.createBuffer(1, sampleRate * 0.5, sampleRate);
                const data = buffer.getChannelData(0);
                
                // Generate a simple impact sound
                for (let i = 0; i < buffer.length; i++) {
                    const t = i / sampleRate;
                    data[i] = Math.sin(t * 400 * Math.PI * 2) * Math.exp(-t * 10);
                }
                
                sound.setBuffer(buffer);
            }
            
            sound.setRefDistance(20);
            sound.setRolloffFactor(1);
            sound.setDistanceModel('linear');
            sound.setLoop(loop);
            sound.setVolume(volume);
            
            // Store in sounds collection
            this.sounds[id] = {
                sound,
                playing: false,
                params: { loop, volume }
            };
            
            return Promise.resolve(sound);
        }
    }
    
    async loadSound(id, url, loop = false, volume = 0.5) {
        // Skip loading if it's in the missing files list
        if (this.missingAudioFiles[id]) {
            return this.loadFallbackSound(id, loop, volume);
        }
        
        return new Promise((resolve, reject) => {
            try {
                //console.log(`Attempting to load sound: ${id} from ${url}`);
                // Create sound
                const sound = new THREE.PositionalAudio(this.listener);
                
                // Load audio file
                this.audioLoader.load(
                    url,
                    (buffer) => {
                        //console.log(`Successfully loaded buffer for sound: ${id}`);
                        sound.setBuffer(buffer);
                        sound.setRefDistance(20);
                        sound.setRolloffFactor(1);
                        sound.setDistanceModel('linear');
                        sound.setLoop(loop);
                        sound.setVolume(volume);
                        
                        // Store in sounds collection
                        this.sounds[id] = {
                            sound,
                            playing: false,
                            params: { loop, volume }
                        };
                        //console.log(`Sound ${id} initialized with volume: ${volume}, loop: ${loop}`);
                        resolve(sound);
                    },
                    (progress) => {
                        //console.log(`Loading progress for ${id}:`, progress);
                    },
                    (error) => {
                        // Mark this file as missing for future reference
                        this.missingAudioFiles[id] = true;
                        
                        // Try to load a fallback instead
                        this.loadFallbackSound(id, loop, volume)
                            .then(resolve)
                            .catch(reject);
                    }
                );
            } catch (error) {
                // Mark this file as missing
                this.missingAudioFiles[id] = true;
                
                // Create fallback and resolve
                this.loadFallbackSound(id, loop, volume)
                    .then(resolve)
                    .catch(reject);
            }
        });
    }
    
    attachSound(soundId, object) {
        if (!this.sounds[soundId]) {
            console.warn(`Sound ${soundId} not found`);
            return;
        }
        
        object.add(this.sounds[soundId].sound);
    }
    
    play(soundId) {
        // Skip verbose logging for all sounds to reduce console noise
        // console.log(`Attempting to play sound: ${soundId}`);
        
        if (this.isMuted) {
            // console.log(`Cannot play ${soundId}: audio is muted`);
            return;
        }
        
        // Check if the sound exists in our collection
        if (!this.sounds[soundId]) {
            // Instead of logging an error, silently handle missing sounds
            if (!this.missingAudioFiles[soundId]) {
                this.missingAudioFiles[soundId] = true;
                // Only log once for missing sounds that aren't already tracked
                console.error(`Cannot play ${soundId}: sound not found in collection`);
            }
            return;
        }
        
        const { sound, playing } = this.sounds[soundId];
       
        if (!sound || !sound.buffer) {
            // Use fallback when possible instead of error
            if (!this.missingAudioFiles[soundId]) {
                this.missingAudioFiles[soundId] = true;
                // Only log once
                console.error(`Cannot play ${soundId}: sound buffer not loaded`);
            }
            return;
        }
        
        // For laser and cube explosion sounds, use playSoundEffect to allow multiple instances
        if (soundId === 'laser' || soundId === 'cube_explode') {
            this.playSoundEffect(soundId);
            return;
        }
        
        // For braking sound, only play if not already playing
        if (soundId === 'braking' && playing) {
            return;
        }
        
        // For other sounds, maintain the original behavior
        if (!playing) {
            sound.play();
            this.sounds[soundId].playing = true;
        } else {
            sound.stop();
            // Reduce noise in console
            // console.log(`Sound ${soundId} is already playing`);
        }
    }
    
    stop(soundId) {
        if (!this.sounds[soundId]) return;
        
        const { sound, playing } = this.sounds[soundId];
        
        if (playing) {
            sound.stop();
            this.sounds[soundId].playing = false;
        }
    }
    
    setVolume(soundId, volume) {
        if (!this.sounds[soundId]) return;
        
        this.sounds[soundId].sound.setVolume(volume);
    }
    
    // Specialized methods for bus sounds
    playEngineIdle() {
        if (!this.isIdleEnabled) return;
        //console.log('Attempting to play engine_idle sound...');
        if (!this.sounds['engine_idle']) {
            console.error('Engine idle sound not found in sounds collection');
            return;
        }
        if (!this.sounds['engine_idle'].sound.buffer) {
            console.error('Engine idle sound buffer not loaded');
        //    return;
        }
        this.play('engine_idle');
        //console.log('Engine idle sound play attempted, playing:', this.sounds['engine_idle'].playing);
    }
    
    playAcceleration() {
        // First pause idle sound
        const idleSound = this.sounds['engine_idle'];
        if (idleSound && idleSound.playing) {
            this.sounds['engine_idle'].sound.pause();
        }
        
        // Play acceleration sound
        this.play('engine_accelerate');
    }
    
    stopAcceleration() {
        this.stop('engine_accelerate');
        
        // Resume idle sound if it was playing
        const idleSound = this.sounds['engine_idle'];
        if (idleSound && idleSound.sound.buffer) {
            idleSound.sound.play();
            idleSound.playing = true;
        }
    }
    
    playBraking() {
        this.play('braking');
    }
    
    stopBraking() {
        this.stop('braking');
    }
    
    playHorn() {
        this.play('horn');
    }
    
    playSoundEffect(soundId) {
        if (this.isMuted || !this.sounds[soundId]) return;
        
        // Get the sound
        const { sound } = this.sounds[soundId];
        
        // Create a temporary container for the sound
        const tempObject = new THREE.Object3D();
        this.scene.add(tempObject);
        
        // Create a new PositionalAudio with the same listener
        const soundClone = new THREE.PositionalAudio(this.listener);
        soundClone.setBuffer(sound.buffer);
        
        // Ensure values are valid before setting them
        if (Number.isFinite(sound.refDistance)) {
            soundClone.setRefDistance(sound.refDistance);
        } else {
            soundClone.setRefDistance(20); // Default value
        }
        
        if (Number.isFinite(sound.rolloffFactor)) {
            soundClone.setRolloffFactor(sound.rolloffFactor);
        } else {
            soundClone.setRolloffFactor(1); // Default value
        }
        
        soundClone.setDistanceModel(sound.distanceModel || 'linear');
        soundClone.setLoop(!!sound.loop);
        
        // Set volume from the sound's parameters
        const baseVolume = this.sounds[soundId].params.volume;
        soundClone.setVolume(baseVolume);
        
        tempObject.add(soundClone);
        
        // Play sound if it's not already playing
        if (sound.buffer) {
            soundClone.play();
            
            // Remove the sound and the temporary object after the sound finishes playing
            setTimeout(() => {
                if (tempObject.parent) {
                    tempObject.remove(soundClone);
                    this.scene.remove(tempObject);
                }
            }, sound.buffer.duration * 1000 + 100);
        }
    }
    
    // Cross-fade duration in milliseconds
    crossFadeDuration = 500;

    // Helper method for cross-fading between sounds
    crossFade(fromSound, toSound, duration) {
        if (!fromSound || !toSound) return;

        const steps = 20;
        const stepTime = duration / steps;
        const volumeStep = 1 / steps;

        let currentStep = 0;
        const fadeInterval = setInterval(() => {
            if (currentStep >= steps) {
                clearInterval(fadeInterval);
                if (fromSound.playing) {
                    fromSound.sound.stop();
                    fromSound.playing = false;
                }
                return;
            }

            const fromVolume = fromSound.params.volume * (1 - (currentStep * volumeStep));
            const toVolume = toSound.params.volume * (currentStep * volumeStep);

            fromSound.sound.setVolume(fromVolume);
            toSound.sound.setVolume(toVolume);

            currentStep++;
        }, stepTime);
    }

    updateEngineSound(speed, acceleration, hasFuel = true) {
        if (!this.isIdleEnabled) return;
        // Check if necessary sounds exist AND their buffers are loaded
        const engineIdle = this.sounds['engine_idle'];
        const engineSteady = this.sounds['engine_steady'];
        const engineAccelerate = this.sounds['engine_accelerate'];
        const engineIdleOff = this.sounds['engine_idle_off'];
    
        if (!engineIdle || !engineIdle.sound.buffer ||
            !engineSteady || !engineSteady.sound.buffer ||
            !engineAccelerate || !engineAccelerate.sound.buffer ||
            !engineIdleOff || !engineIdleOff.sound.buffer) {
            return;
        }

        // Handle no fuel scenario
        if (!hasFuel) {
            // Cross-fade to engine off sound
            if (engineSteady.playing) {
                this.crossFade(engineSteady, engineIdleOff, this.crossFadeDuration);
            } else if (engineIdle.playing) {
                this.crossFade(engineIdle, engineIdleOff, this.crossFadeDuration);
            } else if (engineAccelerate.playing) {
                this.crossFade(engineAccelerate, engineIdleOff, this.crossFadeDuration);
            }
            
            // Play engine idle off sound if not already playing
            if (!engineIdleOff.playing) {
                engineIdleOff.sound.setVolume(0);
                this.play('engine_idle_off');
                engineIdleOff.sound.setVolume(engineIdleOff.params.volume);
            }
            return;
        }
    
        const absSpeed = Math.abs(speed);
        const ZERO_SPEED_THRESHOLD = 0.1; // Speed threshold to consider vehicle stopped

        // Handle zero speed case first
        if (absSpeed <= ZERO_SPEED_THRESHOLD) {
            // Transition to idle when stopped
            if (engineSteady.playing) {
                engineSteady.sound.stop();
                engineSteady.playing = false;
                if (!engineIdle.playing) {
                    engineIdle.sound.setLoop(true);
                    engineIdle.sound.setVolume(engineIdle.params.volume);
                    this.play('engine_idle');
                }
            }
            if (engineAccelerate.playing) {
                this.stopAcceleration();
            }
            return;
        }
    
        // Handle moving speeds
        if (absSpeed >= BUS.HIGH_SPEED_THRESHOLD) {
            if (!engineSteady.playing) {
                engineSteady.sound.setLoop(true);
                if (engineIdle.playing) {
                    this.crossFade(engineIdle, engineSteady, this.crossFadeDuration);
                }
                engineSteady.sound.setVolume(0);
                this.play('engine_steady');
                engineSteady.sound.setVolume(engineSteady.params.volume);
            }
    
            const minPitch = 1.0, maxPitch = 1.4;
            const pitch = minPitch + Math.min((absSpeed - BUS.HIGH_SPEED_THRESHOLD) / 30, 1) * (maxPitch - minPitch);
            engineSteady.sound.setPlaybackRate(pitch);
        } else {
            if (engineSteady.playing) {
                if (!engineIdle.playing) {
                    engineIdle.sound.setLoop(true);
                    engineIdle.sound.setVolume(0);
                    this.play('engine_idle');
                    this.crossFade(engineSteady, engineIdle, this.crossFadeDuration);
                }
            }
            const minIdlePitch = 0.8, maxIdlePitch = 1.6;
            const idlePitch = minIdlePitch + Math.min(absSpeed / 40, 1) * (maxIdlePitch - minIdlePitch);
            engineIdle.sound.setPlaybackRate(idlePitch);
        }
    
        // Optional Acceleration Handling
        if (acceleration > 0.6 && absSpeed < 30 && !engineSteady.playing) {
            if (!engineAccelerate.playing) {
                this.playAcceleration();                 
            }
        } else {
            if (engineAccelerate.playing) {
                this.stopAcceleration();                  
            }
        }
    }
    
    // Stop all sounds - used when game over occurs
    stopAllSounds() {
        // Stop all playing sounds
        Object.keys(this.sounds).forEach(id => {
            if (this.sounds[id].playing) {
                this.sounds[id].sound.stop();
                this.sounds[id].playing = false;
            }
        });
    }
    
    // Set master volume for all sounds
    setMasterVolume(value) {
        this.masterVolume = Math.max(0, Math.min(1, value));
        this.listener.setMasterVolume(this.masterVolume);
    }
    
    // Mute/unmute all sounds
    setMute(value) {
        this.isMuted = value;
        
        if (value) {
            // Stop all playing sounds
            Object.keys(this.sounds).forEach(id => {
                if (this.sounds[id].playing) {
                    this.sounds[id].sound.pause();
                }
            });
        } else {
            // Resume sounds that should be playing
            if (this.sounds['engine_idle']) {
                this.playEngineIdle();
            }
        }
    }
    
    // Clean up resources when no longer needed
    dispose() {
        // Stop and remove all sounds
        Object.keys(this.sounds).forEach(id => {
            if (this.sounds[id].playing) {
                this.sounds[id].sound.stop();
            }
            
            if (this.sounds[id].sound.buffer) {
                this.sounds[id].sound.disconnect();
                // Clear buffer reference to help garbage collection
                this.sounds[id].sound.buffer = null;
            }
            // Remove all event listeners
            this.sounds[id].sound.removeEventListener();
        });
        
        // Clear references
        this.sounds = {};
        
        // Remove listener from camera
        if (this.camera && this.listener) {
            this.camera.remove(this.listener);
            // Clear listener references
            this.listener.context.close();
            this.listener = null;
        }
        
        // Clear other references
        this.camera = null;
        this.scene = null;
        this.audioLoader = null;
        this.initialized = false;
    }
    
    // Create fallback sounds if audio files can't be loaded
    createFallbackSounds() {
        // Create basic oscillator sounds for collision fallbacks
        const createOscillatorBuffer = (frequency, duration, type = 'sine') => {
            const sampleRate = 44100;
            const numSamples = Math.floor(duration * sampleRate);
            const buffer = this.listener.context.createBuffer(1, numSamples, sampleRate);
            const data = buffer.getChannelData(0);
            
            for (let i = 0; i < numSamples; i++) {
                const t = i / sampleRate;
                const phase = frequency * t * Math.PI * 2;
                
                // Simple envelope
                const envelope = Math.min(1, 3 * t) * Math.max(0, 1 - 3 * (t / duration - 0.7));
                
                if (type === 'sine') {
                    data[i] = Math.sin(phase) * envelope;
                } else if (type === 'square') {
                    data[i] = (Math.sin(phase) >= 0 ? 1 : -1) * envelope * 0.7;
                } else if (type === 'noise') {
                    data[i] = (Math.random() * 2 - 1) * envelope * 0.5;
                }
            }
            
            return buffer;
        };
        
        // Create fallback sounds
        try {
            const softCollisionBuffer = createOscillatorBuffer(150, 0.3, 'noise');
            const mediumCollisionBuffer = createOscillatorBuffer(120, 0.5, 'noise');
            const hardCollisionBuffer = createOscillatorBuffer(80, 0.8, 'noise');
            
            // Create sounds
            const softSound = new THREE.PositionalAudio(this.listener);
            softSound.setBuffer(softCollisionBuffer);
            softSound.setVolume(0.5);
            
            const mediumSound = new THREE.PositionalAudio(this.listener);
            mediumSound.setBuffer(mediumCollisionBuffer);
            mediumSound.setVolume(0.7);
            
            const hardSound = new THREE.PositionalAudio(this.listener);
            hardSound.setBuffer(hardCollisionBuffer);
            hardSound.setVolume(0.9);
            
            // Store in sounds collection
            this.sounds['collision_soft'] = { sound: softSound, playing: false, params: { loop: false, volume: 0.5 } };
            this.sounds['collision_medium'] = { sound: mediumSound, playing: false, params: { loop: false, volume: 0.7 } };
            this.sounds['collision_hard'] = { sound: hardSound, playing: false, params: { loop: false, volume: 0.9 } };
            
            //console.log('Created fallback collision sounds');
        } catch (error) {
            //console.error('Failed to create fallback sounds:', error);
        }
    }
    
    // Play collision sound based on impact force
    playCollisionSound(position, impactForce) {
        // Early return if collision sounds are disabled or audio is muted
        if (!this.collisionSoundsEnabled || this.isMuted) return;
        
        let soundId;
        
        // Select the appropriate sound based on impact force
        if (impactForce < 50) {
            soundId = 'collision_soft';
        } else if (impactForce < 150) {
            soundId = 'collision_medium';
        } else {
            soundId = 'collision_hard';
        }
        
        // Handle missing sounds with fallbacks
        if (this.missingAudioFiles[soundId] || !this.sounds[soundId]) {
            // Try fallback to medium if hard is missing
            if (soundId === 'collision_hard' && this.sounds['collision_medium']) {
                soundId = 'collision_medium';
            }
            // Try fallback to soft if medium is missing
            else if (soundId === 'collision_medium' && this.sounds['collision_soft']) {
                soundId = 'collision_soft';
            }
            // If all else fails, just return
            else if (!this.sounds[soundId]) {
                return;
            }
        }
        
        // Get the sound
        const { sound } = this.sounds[soundId];
        
        // Skip if no buffer is loaded
        if (!sound || !sound.buffer) return;
        
        // Create a temporary container for the sound at the collision position
        // This is used so we can properly position the sound in 3D space
        const tempObject = new THREE.Object3D();
        tempObject.position.copy(position);
        this.scene.add(tempObject);
        
        // Clone the sound for multiple simultaneous collisions
        const soundClone = sound.clone();
        tempObject.add(soundClone);
        
        // Scale volume based on impact force (within reasonable limits)
        const normalizedForce = Math.min(1.0, impactForce / 200);
        const baseVolume = this.sounds[soundId].params.volume;
        soundClone.setVolume(baseVolume * (0.7 + normalizedForce * 0.3));
        
        // Play sound if it's not already playing
        if (sound.buffer) {
            soundClone.play();
            
            // Remove the sound and the temporary object after the sound finishes playing
            setTimeout(() => {
                if (tempObject.parent) {
                    tempObject.remove(soundClone);
                    this.scene.remove(tempObject);
                }
            }, sound.buffer.duration * 1000 + 100);
        }
    }
    
    // Method to enable or disable collision sounds
    setCollisionSoundsEnabled(enabled) {
        this.collisionSoundsEnabled = enabled;
    }
}

export default AudioManager;